import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../hooks/useSession';
import { ApiError } from '../lib/api';
import { Input } from '@/components/ui/input';
import { Password } from '../admin/ui';

/**
 * Vendor sign-in.
 *
 * Credentials are the vendor's normal marketplace login — VU forwards them server-side
 * and never stores them. The marketplace decides whether the account is approved, and
 * its refusal messages ("under review", "rejected") are shown verbatim because they are
 * better and more accurate than anything we would write here.
 */
export function Login() {
  const { signIn } = useSession();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const role = await signIn(email, password);
      // Same form, same credentials field — only the destination differs by role.
      navigate(role === 'admin' ? '/admin/overview' : '/');
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not sign in. Please check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 'var(--space-6)', background: 'var(--vu-ink)' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-6)' }}>
          <img
            src="/shipindia-mark.png"
            width={44}
            height={44}
            alt=""
            aria-hidden="true"
            style={{ display: 'block', margin: '0 auto var(--space-3)', objectFit: 'contain' }}
          />
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-h4)', color: '#fff' }}>
            ShipIndia
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-h2)', color: '#fff' }}>
            Vendor University
          </div>
          <div
            style={{
              fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '.12em',
              textTransform: 'uppercase', color: 'var(--brand-accent)', marginTop: 6,
            }}
          >
            Sell India to the world
          </div>
        </div>

        <form className="vu-card" onSubmit={onSubmit}>
          <h1 className="vu-h3" style={{ marginBottom: 'var(--space-2)' }}>Sign in</h1>
          <p className="vu-muted" style={{ marginTop: 0, marginBottom: 'var(--space-5)', fontSize: 'var(--fs-sm)' }}>
            Vendors sign in with their ShipIndia marketplace credentials.
          </p>

          <label style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            <span style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--text-strong)' }}>Email</span>
            <Input
              className="vu-input"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 'var(--space-5)' }}>
            <span style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--text-strong)' }}>Password</span>
            <Password value={password} onChange={setPassword} />
          </label>

          {error && (
            <div
              role="alert"
              style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--fs-sm)' }}
            >
              {error}
            </div>
          )}

          <button type="submit" className="vu-btn" style={{ width: '100%' }} disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 'var(--space-5)', color: 'rgba(255,255,255,.6)', fontSize: 'var(--fs-sm)' }}>
          Vendor University is open to approved ShipIndia vendors.
        </p>
      </div>
    </div>
  );
}
